import React, { useState, useEffect, useCallback } from 'react';
import { Save } from 'lucide-react';
import { useAuth } from '../../../components/context/AuthContext';
import axios from 'axios';
import ProfileImage from '../ProfileImage';

const ProfileTab = ({ onError, onSuccess }) => {
  const { auth, updateUser } = useAuth();

  const handleImageUpdate = (imageUrl) => {
    updateUser({ id: auth.user.id, profile_image: imageUrl });
  };
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
  });
  const [profileEmail, setProfileEmail] = useState("");

  const loadProfileData = useCallback(async () => {
    try {
      const response = await axios.get("/api/user");
      const data = response.data;

      setProfileEmail(data.email || "");
      setFormData(prev => ({
        ...prev,
        first_name: data.first_name || "",
        last_name: data.last_name || "",
      }));
    } catch (err) {
      console.error("Error loading profile data:", err);
      onError(err.response?.data?.error || "Error cargando datos del perfil");
    }
  }, [onError]);

  useEffect(() => {
    if (auth.token) {
      loadProfileData();
    }
  }, [auth.token, loadProfileData]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (!formData.first_name || !formData.last_name) {
        onError("El nombre y apellido son requeridos");
        return;
      }

      const updateData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
      };

      const response = await axios.put("/api/user", updateData);
      updateUser(response.data);
      onSuccess("Perfil actualizado con éxito");
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Profile update error:", err);
      onError(err.response?.data?.error || "Error actualizando el perfil");
      window.scrollTo(0, 0);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-lg p-4 md:p-8">
      <form onSubmit={handleProfileUpdate} className="space-y-6 md:space-y-8">
        {/* Header */}
        <div className="pb-6 border-b border-white/10">
          <div className="flex flex-col items-center gap-5 mb-6">
            <div className="scale-125 md:scale-[1.35]">
              <ProfileImage profileData={auth.user} onImageUpdate={handleImageUpdate} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/40 flex items-center justify-center backdrop-blur-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">Tu Información</h3>
              <p className="text-white/70 text-sm">Actualiza tus datos personales</p>
            </div>
          </div>
        </div>

        {/* Form Section - Personal Info */}
        <div className="space-y-4">
          <h4 className="text-white/90 font-semibold text-sm uppercase tracking-wide">Información Personal</h4>

          <div className="space-y-2">
            <label className="block text-white/90 text-sm md:text-base font-medium" htmlFor="profile-readonly-email">
              Correo electrónico
            </label>
            <input
              id="profile-readonly-email"
              type="email"
              readOnly
              autoComplete="email"
              aria-readonly="true"
              className="w-full px-4 py-3 md:py-2.5 backdrop-blur-md bg-white/5 border border-white/15 rounded-xl text-white/90 cursor-default
              focus:outline-none"
              value={profileEmail || auth?.user?.email || ""}
            />
          </div>

          {/* Name & Last Name - Stack on mobile, Side by side on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-white/90 text-sm md:text-base font-medium">
                Nombre <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="Tu nombre"
                className="w-full px-4 py-3 md:py-2.5 backdrop-blur-md bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40
                focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-200"
                value={formData.first_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    first_name: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-white/90 text-sm md:text-base font-medium">
                Apellido <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="Tu apellido"
                className="w-full px-4 py-3 md:py-2.5 backdrop-blur-md bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40
                focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-200"
                value={formData.last_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    last_name: e.target.value,
                  }))
                }
                required
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-3 pt-4 border-t border-white/10">
          <button
            type="submit"
            className="flex-1 px-4 md:px-6 py-4 md:py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700
            text-white rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            disabled={isSaving}
          >
            {isSaving ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <Save className="w-5 h-5" />
            )}
            <span className="font-medium text-sm md:text-base">{isSaving ? "Guardando..." : "Guardar Cambios"}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileTab; 